import { Component, DestroyRef, Input, OnInit } from '@angular/core';
import { CompanyData, SubscriptionDto } from '../../../interfaces';
import { SubscriptionService } from '../../../services/subscription.service';
import { BILLING_STATUS, SUBSCRIPTION_PLAN } from '../../../enums';
import { BehaviorSubject, EMPTY, map, Observable, switchMap, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CompanyService } from '../../../services/company.service';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-subscription-info',
  templateUrl: './subscription-info.component.html',
  styleUrls: ['./subscription-info.component.scss']
})
export class SubscriptionInfoComponent implements OnInit {
  @Input({ required: true }) company!: CompanyData;

  protected readonly BILLING_STATUS = BILLING_STATUS;
  protected currentSubscription$: BehaviorSubject<SubscriptionDto | null> = new BehaviorSubject<SubscriptionDto | null>(null);
  protected warningMessage$: Observable<string> = this.currentSubscription$.pipe(
    map((currentSubscription) => {
      const isExpiredTrial = currentSubscription === null && this.company.billing?.status === BILLING_STATUS.PAST_DUE;

      if (isExpiredTrial) {
        return this.translateService.instant('subscription.warning.trialEnded');
      }

      if (this.company.billing?.status === BILLING_STATUS.PAST_DUE) {
        return this.translateService.instant('subscription.warning.pastDue');
      }

      if (this.company.billing?.status === BILLING_STATUS.UNSUBSCRIBED) {
        return this.translateService.instant('subscription.warning.unsubscribed');
      }

      if (this.company.billing?.status === BILLING_STATUS.SUSPENDED) {
        return this.translateService.instant('subscription.warning.readOnly');
      }
    })
  )

  constructor(
    protected companyService: CompanyService,
    private dialogService: MatDialog,
    private subscriptionService: SubscriptionService,
    private translateService: TranslateService,
    private destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    this.subscriptionService.getSubscriptionByCompanyId(this.company.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.currentSubscription$)
  }

  isTrial(): boolean {
    return this.company.billing?.status === BILLING_STATUS.FREE_TRIAL
  }

  isActive(): boolean {
    return this.company.billing?.status === BILLING_STATUS.SMALL || this.company.billing?.status === BILLING_STATUS.MEDIUM;
  }

  shouldDisplayTimer(): boolean {
    return this.company.billing?.status === BILLING_STATUS.FREE_TRIAL || this.company.billing?.status === BILLING_STATUS.PAST_DUE;
  }

  shouldDisplayWarning(): boolean {
    return this.company.billing?.status === BILLING_STATUS.SUSPENDED
      || this.company.billing?.status === BILLING_STATUS.UNSUBSCRIBED
      || this.company.billing?.status === BILLING_STATUS.PAST_DUE;
  }

  shouldDisplayStatusName(currentSubscription: SubscriptionDto | null): boolean {
    return this.company.billing?.status === BILLING_STATUS.UNSUBSCRIBED
      || this.company.billing?.status === BILLING_STATUS.FREE_TRIAL
      || !currentSubscription;
  }

  shouldDisplayActionMenu(): boolean {
    return this.company.billing?.status !== BILLING_STATUS.SUSPENDED
      && this.company.billing?.status !== BILLING_STATUS.UNSUBSCRIBED
      && this.company.billing?.status !== BILLING_STATUS.FREE_TRIAL
      && this.company.billing?.status !== BILLING_STATUS.FREE;
  }

  async selectPlan(): Promise<void> {
    const { ChangePlanModalComponent } = await import('../../modals/subscription/change-plan-modal/change-plan-modal.component');

    this.dialogService.open(ChangePlanModalComponent, {
      panelClass: ['dialog-overlay-pane'],
      width: '590px',
      data: {
        companyId: this.company.id,
        discount: this.company.billing?.discountPercentage
      }
    });
  }

  renewSubscription(): void {
    const plan = this.currentSubscription$.getValue()?.plan.name;

    this.subscriptionService.updateSubscriptionRequest(plan || SUBSCRIPTION_PLAN.SMALL).subscribe();
  }

  async cancelSubscription(): Promise<void> {
    const { SubscriptionCancelConfirmationComponent } = await import('../../modals/subscription/subscription-cancel-confirmation/subscription-cancel-confirmation.component');
    const { SubscriptionCanceledModalComponent } = await import('../../modals/subscription/subscription-canceled-modal/subscription-canceled-modal.component');

    const cancelDialogRef = this.dialogService.open(SubscriptionCancelConfirmationComponent, {
      panelClass: ['dialog-overlay-pane'],
      width: '500px',
      data: {
        periodEndDate: this.company.billing!.nextPaymentDueAt
      }
    });

    cancelDialogRef.afterClosed().pipe(
      switchMap((result: unknown) => {
        if (result !== 'confirm') {
          return EMPTY;
        }

        return this.subscriptionService.unsubscribeRequest();
      }),
    ).subscribe((response) => {
      if (!response.success) {
        return;
      }

      this.dialogService.open(SubscriptionCanceledModalComponent, {
        panelClass: ['dialog-overlay-pane'],
        width: '500px',
        data: {
          periodEndDate: this.company.billing!.nextPaymentDueAt
        }
      });
    })
  }
}
